import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBookingFlowStore, SelectedDateTime } from '../../stores/useBookingFlowStore';
import { bookingApi } from '../../services/api';
import ProgressBar from '../../components/booking/ProgressBar';
import { toast } from 'react-hot-toast';

interface TimeSlot {
  time: string; // ISO string
  displayTime: string; // HH:mm
}

const DateTimePage: React.FC = () => {
  const { t } = useTranslation(['pages', 'translation']);
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const { service, dateTime: selectedDateTime, setDateTime, canProceedToDateTime } = useBookingFlowStore();

  // 計算今天的日期字串（使用本地時區）
  const getTodayDateString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const today = getTodayDateString();

  const [selectedDate, setSelectedDate] = useState<string>(today);
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // 驗證是否有選擇服務
  useEffect(() => {
    if (!canProceedToDateTime()) {
      navigate(`/book/${storeId}`);
    }
  }, [canProceedToDateTime, navigate, storeId]);

  // 初次載入時，自動尋找下一個有可預約時段的日期
  useEffect(() => {
    if (isInitialLoad && service) {
      findNextAvailableDate();
    }
  }, [isInitialLoad, service]);

  // 當選擇日期時，獲取可預約時段
  useEffect(() => {
    if (!isInitialLoad && selectedDate && service) {
      fetchAvailableSlots();
    }
  }, [selectedDate]);

  // 自動尋找下一個有可預約時段的日期
  const findNextAvailableDate = async () => {
    if (!service) return;

    setLoading(true);
    let foundDate: string | null = null;
    const maxDaysToCheck = 7; // 最多查找7天
    let hasError = false;

    try {
      for (let i = 0; i < maxDaysToCheck; i++) {
        const checkDate = new Date();
        checkDate.setDate(checkDate.getDate() + i);
        const dateStr = `${checkDate.getFullYear()}-${String(checkDate.getMonth() + 1).padStart(2, '0')}-${String(checkDate.getDate()).padStart(2, '0')}`;

        try {
          const response = await bookingApi.getAvailableSlots({
            storeId: storeId!,
            serviceId: service.id,
            date: dateStr,
          });

          let slots = response.data.availableSlots || [];

          // ✅ 如果是今天，過濾掉已經過去的時段
          const now = new Date();
          const isToday = dateStr === getTodayDateString();

          if (isToday) {
            const currentHour = now.getHours();
            const currentMinute = now.getMinutes();

            slots = slots.filter((timeStr: string) => {
              const [slotHour, slotMinute] = timeStr.split(':').map(Number);
              // 只保留未來的時段（至少30分鐘後）
              return (
                slotHour > currentHour ||
                (slotHour === currentHour && slotMinute > currentMinute + 30)
              );
            });
          }

          if (slots.length > 0) {
            foundDate = dateStr;
            const timeSlots: TimeSlot[] = slots.map((timeStr: string) => ({
              time: `${dateStr}T${timeStr}:00`,
              displayTime: timeStr,
            }));
            setAvailableSlots(timeSlots);
            break;
          }
        } catch (error: any) {
          // 單日查詢失敗，記錄但繼續查詢下一天
          console.error(`Failed to fetch slots for ${dateStr}:`, error);
          // 只在第一天就失敗時標記錯誤（表示可能是系統問題）
          if (i === 0) {
            hasError = true;
            break;
          }
        }
      }

      // 先設置 isInitialLoad 為 false，避免觸發額外的 useEffect
      setIsInitialLoad(false);

      if (foundDate) {
        setSelectedDate(foundDate);
      } else if (hasError) {
        // 發生系統錯誤（第一天就失敗）
        setAvailableSlots([]);
        toast.error(t('pages:booking.dateTime.loadSlotsError'));
      } else {
        // 如果7天內都沒有可預約時段
        setAvailableSlots([]);
        toast.error(t('pages:booking.dateTime.noSlotsInWeek'));
      }
    } catch (error: any) {
      console.error('Failed to find next available date:', error);
      setIsInitialLoad(false);
      setAvailableSlots([]);
      toast.error(t('pages:booking.dateTime.loadSlotsError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableSlots = async () => {
    if (!selectedDate || !service) return;

    // ✅ 移除快取機制，每次都查詢最新資料（避免重複預約問題）
    try {
      setLoading(true);
      const response = await bookingApi.getAvailableSlots({
        storeId: storeId!,
        serviceId: service.id,
        date: selectedDate,
      });

      // 轉換 API 返回的字串陣列為 TimeSlot 物件陣列
      let slots: TimeSlot[] = (response.data.availableSlots || []).map((timeStr: string) => ({
        time: `${selectedDate}T${timeStr}:00`, // ISO string
        displayTime: timeStr, // HH:mm
      }));

      // ✅ 如果選擇的是今天，過濾掉已經過去的時段
      const now = new Date();
      const isToday = selectedDate === getTodayDateString();

      if (isToday) {
        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();

        slots = slots.filter((slot) => {
          const [slotHour, slotMinute] = slot.displayTime.split(':').map(Number);
          // 只保留未來的時段（至少30分鐘後）
          return (
            slotHour > currentHour ||
            (slotHour === currentHour && slotMinute > currentMinute + 30)
          );
        });

        // 如果今天所有時段都已過，自動跳到下一個有可預約時段的日期
        if (slots.length === 0) {
          toast(t('pages:booking.dateTime.todaySlotsEnded'), { icon: '⏰' });
          await findNextAvailableDate();
          return;
        }
      }

      setAvailableSlots(slots);

      // 不在這裡顯示 toast，因為 UI 已經有顯示訊息
    } catch (error: any) {
      console.error('Failed to fetch available slots:', error);
      // 不顯示 toast，因為 UI 有「此日期沒有可預約的時段」的顯示
      // 如果是真的錯誤（如網路問題），使用者可以重新選擇日期
      setAvailableSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSlotSelect = (slot: TimeSlot) => {
    setSelectedSlot(slot);
  };

  const handleNext = () => {
    if (!selectedDate || !selectedSlot) {
      toast.error(t('pages:booking.dateTime.selectDateTimeError'));
      return;
    }

    const dateTimeData: SelectedDateTime = {
      date: selectedDate,
      time: selectedSlot.time,
      displayTime: selectedSlot.displayTime,
    };

    setDateTime(dateTimeData);
    navigate(`/book/${storeId}/staff`);
  };

  // 計算最大日期（3個月後）
  const maxDate = new Date();
  maxDate.setMonth(maxDate.getMonth() + 3);
  const maxDateStr = maxDate.toISOString().split('T')[0];

  if (!service) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-8 px-4">
      <div className="container mx-auto max-w-4xl">
        {/* 進度條 */}
        <ProgressBar
          currentStep={2}
          totalSteps={4}
          steps={[
            t('pages:booking.storeHome.selectService'),
            t('pages:booking.dateTime.title'),
            t('pages:booking.info.title'),
            t('pages:booking.confirmation.successTitle')
          ]}
        />

        {/* 返回按鈕 */}
        <button
          onClick={() => navigate(`/book/${storeId}`)}
          className="mb-6 flex items-center space-x-2 text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span>{t('pages:booking.dateTime.backToService')}</span>
        </button>

        {/* 頁面標題 */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
            {t('pages:booking.dateTime.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {t('pages:booking.dateTime.selectedService')}<span className="font-semibold">{service.name}</span> ({service.duration} {t('translation:minutes')} / ${Math.floor(service.price)})
          </p>
        </div>

        {/* 日期選擇 */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            {t('pages:booking.dateTime.selectDate')}
          </h2>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => {
              setSelectedDate(e.target.value);
              setSelectedSlot(null); // 重置時段選擇
            }}
            min={today}
            max={maxDateStr}
            className="w-full px-4 py-3 text-lg border-2 border-slate-300 dark:border-slate-600 rounded-lg focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:border-brand-500 dark:bg-slate-700 dark:text-white transition-colors dark:[color-scheme:dark]"
            style={{ colorScheme: 'dark' }}
          />
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            可預約日期：今天至 {maxDateStr}
          </p>
        </div>

        {/* 時間段選擇 */}
        {selectedDate && (
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              {t('pages:booking.dateTime.selectTime')}
              {loading && <span className="ml-2 text-sm text-gray-500">({t('pages:booking.dateTime.loading')})</span>}
            </h2>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
              </div>
            ) : availableSlots.length > 0 ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                {availableSlots.map((slot) => (
                  <button
                    key={slot.time}
                    onClick={() => handleSlotSelect(slot)}
                    className={`
                      py-3 px-4 rounded-lg font-semibold text-sm transition-all duration-200
                      ${
                        selectedSlot?.time === slot.time
                          ? 'bg-brand-600 text-white ring-2 ring-brand-300 dark:ring-brand-700 shadow-lg scale-105'
                          : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-brand-100 dark:hover:bg-brand-900 hover:text-brand-600 dark:hover:text-brand-400'
                      }
                    `}
                  >
                    {slot.displayTime}
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-4xl mb-4">📅</div>
                <p className="text-gray-500 dark:text-gray-400 text-lg">
                  {t('pages:booking.dateTime.noSlotsAvailable')}
                </p>
                <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">
                  {t('pages:booking.dateTime.selectOtherDate')}
                </p>
              </div>
            )}

            {/* 下一步按鈕 */}
            {availableSlots.length > 0 && (
              <div className="mt-8 flex justify-end">
                <button
                  onClick={handleNext}
                  disabled={!selectedSlot}
                  className={`
                    px-8 py-3 rounded-lg font-bold text-white transition-all duration-200
                    ${
                      selectedSlot
                        ? 'bg-brand-600 hover:bg-brand-700 shadow-lg hover:shadow-xl'
                        : 'bg-gray-300 dark:bg-slate-700 cursor-not-allowed'
                    }
                  `}
                >
                  {t('pages:booking.dateTime.nextStep')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* 未選擇日期的提示 */}
        {!selectedDate && (
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-12 text-center">
            <div className="text-6xl mb-4">📅</div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              {t('pages:booking.dateTime.pleaseSelectDate')}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {t('pages:booking.dateTime.selectDateMessage')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DateTimePage;
